import React, { useEffect, useState } from "react";
import "./setup.css";
import { GetBridgePathNoAuth } from "../helpers/router";
import healthy from "../img/healthy.svg";
import lock from "../img/1passwordlock.svg";
import { useRedirect } from "../helpers/redirect";

const Setup = (): JSX.Element => {
  const [domain, setDomain] = useState(window.location.hostname);
  const [failedVerify, setFailedVerify] = useState("");
  const [configureDomain, setConfigureDomain] = useState("");
  const [connectVerified, setConnectVerified] = useState(false);
  const [dnsStatus, setDnsStatus] = useState("unknown");
  const [dnsDescription, setDnsDescription] = useState("");

  const state = useRedirect();

  useEffect(() => {

    // regexp for detecting IPv4 addresses
    const ipv4 = '(?:25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]\\d|\\d)(?:\\.(?:25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]\\d|\\d)){3}';
    if ((window.location.hostname).match(ipv4)) {
        setDnsDescription("Configure a DNS record for your SCIM bridge, and replace the IP address with your domain name:");
    } else {
        setDnsDescription("Configure a DNS record for your SCIM bridge, and confirm the domain name below is correct:");
    }

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("verified") || window.location.protocol === "https:") {
      setFailedVerify("");

      const currentDomain =
        window.location.hostname + " configuration successful.";
      console.log(currentDomain);
      setDnsStatus("checked healthy");
      setConfigureDomain(currentDomain);
      setConnectVerified(true);
    }
  }, []);

  const buildVerifyRequest = (current: string, domain: string): string => {
    const verifyAddress = window.location.protocol + "//" + current + "/verify";
    return verifyAddress + "?domain=" + domain;
  };

  const getTLSCert = async (domain: string): Promise<void> => {
    const current = window.location.hostname;
    const url = buildVerifyRequest(current, domain);

    try {
      const resp = await fetch(url);
      if (resp.status > 299) {
        throw new Error("domain could not be verified");
      }
    } catch (err) {
      throw new Error("getTLSCert error: " + err);
    }
  };

  const getPath = async (path: string): Promise<string> => {
    try {
      const response = await GetBridgePathNoAuth(path + "/health");
      if (!response.ok) {
        throw new Error("Request failed: " + response.text);
      } else {
        return path;
      }
    } catch (err) {
      throw new Error("Failed to get '" + path + "': " + err);
    }
  };

  // getRoutablePath gets a valid URI that can be resolved.
  // Guarantees: if this function returns, it is the new valid path to redirect to. Otherwise an error is thrown.
  const getRoutablePath = async (
    oldPath: string,
    newPath: string,
    retries: number
  ): Promise<string> => {
    try {
      if (oldPath === "http://localhost") {
        return await Promise.resolve(getPath(newPath));
      }
      const path = await Promise.any([getPath(oldPath), getPath(newPath)]);
      return path;
    } catch (err) {
      if (retries === 0) {
        throw new Error("Out of retries: " + err);
      }
      console.log("1Password SCIM Bridge TLS upgrade not ready. Retrying...");

      // pause between tries
      await new Promise((resolve) => setTimeout(resolve, 1000));

      return getRoutablePath(oldPath, newPath, retries - 1);
    }
  };

  const testDomain = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const regexp = /(^\w+:|^)\/\/|(\/)/g;

    const newDomain = domain.replace(regexp, "");

    if (newDomain === "localhost" || newDomain === "127.0.0.1") {
      setConnectVerified(true);
      setDnsStatus("checked healthy");
      window.location.replace("/app/setup?verified=true");
      return;
    }

    const oldPath = "http://" + window.location.hostname;
    const newPath = "https://" + newDomain;

    try {
      await getTLSCert(newDomain);
      setFailedVerify("");

      setConfigureDomain("Verifying " + newDomain + "...");
    } catch (err) {
      console.log("getTLSCert error: " + err);
      setFailedVerify(
        "Couldn't verify domain. Check your configuration and try again. Ensure the DNS record has had time to propagate, and that port 443 is open on your firewall."
      );
      return;
    }

    try {
      const path = await getRoutablePath(oldPath, newPath, 35);
      console.log("PATH", path, newPath);
      if (path === newPath) {
        window.location.replace(path);
      } else {
        setFailedVerify(
          "The 1Password SCIM bridge isn’t responding. Make sure the bridge is still working and available on the network."
        );
        setConfigureDomain("");
      }
    } catch (err) {
      console.log("Failed to resolve TLS upgrade: " + err);
      setFailedVerify(
        "LetsEncrypt challenge attempt failed. Verify that your SCIM bridge's domain name has not been rate limited."
      );
      setConfigureDomain("");
    }
  };

  if (state.loading) {
    return <></>;
  }

  if (state.sessionFileFound) {
    window.location.replace("/app/login");
  }


  return (
    <div>
      <h2>1Password SCIM Bridge Setup</h2>
      <div className="row deployment-status">
        <section className="status">
          <img
            alt="healthy"
            src={healthy}
            className="healthy checked"
            id="deployment-checkmark"
          />
        </section>
        <section className="description">
          <h2>Deploy</h2>
          <p>Deployment successful.</p>
        </section>
      </div>
      <div className="row dns-status">
        <section className="status">
          <img
            alt="healthy"
            src={healthy}
            className={dnsStatus}
            id="dns-status-healthy"
          />
        </section>
        <section className="description">
          <h2>Configure domain</h2>
          {!configureDomain && (
            <div id="configure-domain">
              <p id="dns-description">
                  {dnsDescription}
              </p>
              <form onSubmit={testDomain} id="domain-checker-form">
                <input
                  type="text"
                  id="letsencrypt-domain-field"
                  value={domain}
                  onChange={(e) => setDomain(e.currentTarget.value)}
                />
                <button type="submit" className="verify">
                  Verify
                </button>
              </form>
              <p id="letsencrypt-info">
                The above domain will be sent to
                the{" "}
                <a href="https://letsencrypt.org/" target="blank">
                  Let's Encrypt
                </a>{" "}
                service for the purposes of acquiring a TLS certificate for your SCIM bridge.<br /><br />

                If successful, you will be automatically redirected to a TLS-secured connection.
              </p>
            </div>
          )}
          {configureDomain && (
            <p id="configure-domain-verified">{configureDomain}</p>
          )}
          {failedVerify && (
            <p id="failed-verification" className="errormsg">
              {failedVerify}
            </p>
          )}
        </section>
      </div>
      <div className="row account-configure">
        <section className="status">
          <img
            alt="healthy"
            src={healthy}
            className="healthy"
            id="account-configure-status"
          />
        </section>
        <section className="description">
          <h2>Connect account</h2>
          {connectVerified && (
            <div>
              {" "}
              <p id="connect-description">Sign in to connect with 1Password:</p>
              <button
                className="onepassword-redirect"
                onClick={() => window.location.replace("/signinredirect")}
              >
                <img alt="lock" src={lock} className="tinyimg" />
                Sign in with 1Password
              </button>{" "}
            </div>
          )}
          {!connectVerified && (
            <p id="waiting-verification">Domain configuration required.</p>
          )}
        </section>
      </div>
    </div>
  );
};

export default Setup;
